import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: `${BASE}/api` });

const TOKEN_KEY = "supera_token";
const USER_KEY = "supera_user";

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Sesión única / expiración: ante un 401, cerrar sesión y redirigir con motivo.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || "";
    const url = error?.config?.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/register");
    if (status === 401 && !isAuthCall) {
      const wasLoggedIn = !!localStorage.getItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      if (wasLoggedIn && typeof window !== "undefined") {
        const replaced = detail.includes("otro dispositivo");
        try {
          sessionStorage.setItem(
            "supera_logout_reason",
            replaced ? "Se inició sesión con esta cuenta en otro dispositivo. Por seguridad, esta sesión se cerró." : "Tu sesión finalizó. Inicia sesión de nuevo."
          );
        } catch {}
        const path = window.location.pathname;
        const target = path.startsWith("/admin") ? "/admin" : "/login";
        if (path !== target) window.location.href = target;
      }
    }
    return Promise.reject(error);
  }
);

export function saveSession(data) {
  if (data?.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data?.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
}

export function getUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); }
  catch { return null; }
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(HIJO_KEY);
}

// ---- "Hijo/a activo" (vista de padres/madres, solo lectura) ----
const HIJO_KEY = "supera_hijo_activo";
export function getHijoActivo() { return localStorage.getItem(HIJO_KEY) || ""; }
export function setHijoActivo(id) {
  if (id) localStorage.setItem(HIJO_KEY, id);
  else localStorage.removeItem(HIJO_KEY);
}
export function isParentAccount() { return getUser()?.programa === "Padres y Madres"; }
export const COACH_PROGRAMS = ["Coach Junior Supera", "Head Coach Junior Supera"];
export function isCoach() { return COACH_PROGRAMS.includes(getUser()?.programa); }
export function isParentView() { return isParentAccount() && !!getHijoActivo(); }
// Inyecta hijo_id solo cuando la cuenta es de padre/madre y hay un hijo/a activo.
function hijoParam(extra = {}) {
  const id = getHijoActivo();
  return isParentAccount() && id ? { ...extra, hijo_id: id } : extra;
}

export const authApi = {
  async register(payload) {
    const { data } = await api.post("/auth/register", payload);
    saveSession(data);
    return data;
  },
  async login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    saveSession(data);
    return data;
  },
  async me() {
    const { data } = await api.get("/auth/me");
    return data;
  },
};

export const guestApi = {
  async content() { return (await api.get("/guests/content")).data; },
  async webinars(tipo = "upcoming") { return (await api.get(`/guests/webinars?tipo=${tipo}`)).data; },
  async registerWebinar(id) { return (await api.post(`/guests/webinars/${id}/register`)).data; },
  async updateContent(payload) { return (await api.put("/admin/guests/content", payload)).data; },
  async adminWebinars() { return (await api.get("/admin/guests/webinars")).data; },
  async createWebinar(payload) { return (await api.post("/admin/guests/webinars", payload)).data; },
  async updateWebinar(id, payload) { return (await api.put(`/admin/guests/webinars/${id}`, payload)).data; },
  async deleteWebinar(id) { return (await api.delete(`/admin/guests/webinars/${id}`)).data; },
  async leads() { return (await api.get("/admin/guests/leads")).data; },
  // Lead scoring + verificación
  async event(type) { try { return (await api.post("/guests/event", { type })).data; } catch { return null; } },
  async status() { return (await api.get("/guests/me/status")).data; },
  async sendEmailCode() { return (await api.post("/guests/verify-email/send")).data; },
  async confirmEmailCode(code) { return (await api.post("/guests/verify-email/confirm", { code })).data; },
  async confirmWhatsapp() { return (await api.post("/guests/confirm-whatsapp")).data; },
  // SUPERA CRM (admin)
  async crmProspects() { return (await api.get("/admin/crm/prospects")).data; },
  async crmUpdate(id, payload) { return (await api.put(`/admin/crm/prospects/${id}`, payload)).data; },
  async crmConfig() { return (await api.get("/admin/crm/config")).data; },
  async crmSaveConfig(payload) { return (await api.put("/admin/crm/config", payload)).data; },
};

export const correosApi = {
  async list(box = "sent") { return (await api.get(`/admin/emails?box=${box}`)).data; },
  async get(id) { return (await api.get(`/admin/emails/${id}`)).data; },
  async remove(id) { return (await api.delete(`/admin/emails/${id}`)).data; },
  async restore(id) { return (await api.post(`/admin/emails/${id}/restore`)).data; },
  async purge(id) { return (await api.delete(`/admin/emails/${id}/purge`)).data; },
  async emptyTrash() { return (await api.post("/admin/emails/empty-trash")).data; },
  async templates() { return (await api.get("/admin/email-templates")).data; },
  async saveTemplate(key, payload) { return (await api.put(`/admin/email-templates/${key}`, payload)).data; },
  async test(payload) { return (await api.post("/admin/emails/test", payload)).data; },
};

export const nexusApi = {
  // Usuario
  async config() { return (await api.get("/nexus/config")).data; },
  async types() { return (await api.get("/nexus/meeting-types")).data; },
  async slots(id) { return (await api.get(`/nexus/meeting-types/${id}/slots`)).data; },
  async book(payload) { return (await api.post("/nexus/book", payload)).data; },
  async myBookings() { return (await api.get("/nexus/my-bookings")).data; },
  async cancel(id) { return (await api.post(`/nexus/bookings/${id}/cancel`)).data; },
  async ics(id) { return (await api.get(`/nexus/bookings/${id}/ics`, { responseType: "blob" })).data; },
  // Admin
  async adminConfig() { return (await api.get("/admin/nexus/config")).data; },
  async saveConfig(payload) { return (await api.put("/admin/nexus/config", payload)).data; },
  async adminTypes() { return (await api.get("/admin/nexus/meeting-types")).data; },
  async createType(payload) { return (await api.post("/admin/nexus/meeting-types", payload)).data; },
  async updateType(id, payload) { return (await api.put(`/admin/nexus/meeting-types/${id}`, payload)).data; },
  async deleteType(id) { return (await api.delete(`/admin/nexus/meeting-types/${id}`)).data; },
  async hosts() { return (await api.get("/admin/nexus/hosts")).data; },
  async adminBookings() { return (await api.get("/admin/nexus/bookings")).data; },
  async updateBooking(id, payload) { return (await api.put(`/admin/nexus/bookings/${id}`, payload)).data; },
  async metrics(dias = 0) { return (await api.get(`/admin/nexus/metrics?dias=${dias}`)).data; },
};

export const inversoresApi = {
  // Admin (super admin)
  async meta() { return (await api.get("/admin/inversores/meta")).data; },
  async list() { return (await api.get("/admin/inversores")).data; },
  async create(payload) { return (await api.post("/admin/inversores", payload)).data; },
  async detail(id) { return (await api.get(`/admin/inversores/${id}`)).data; },
  async update(id, payload) { return (await api.put(`/admin/inversores/${id}`, payload)).data; },
  async remove(id) { return (await api.delete(`/admin/inversores/${id}`)).data; },
  async addContent(id, formData) { return (await api.post(`/admin/inversores/${id}/contenido`, formData)).data; },
  async updateContent(cid, formData) { return (await api.put(`/admin/inversores/contenido/${cid}`, formData)).data; },
  async deleteContent(cid) { return (await api.delete(`/admin/inversores/contenido/${cid}`)).data; },
  async addMonth(id, payload) { return (await api.post(`/admin/inversores/${id}/meses`, payload)).data; },
  async updateMonth(mid, payload) { return (await api.put(`/admin/inversores/meses/${mid}`, payload)).data; },
  async deleteMonth(mid) { return (await api.delete(`/admin/inversores/meses/${mid}`)).data; },
  async addPay(id, formData) { return (await api.post(`/admin/inversores/${id}/pagos`, formData)).data; },
  async deletePay(pid) { return (await api.delete(`/admin/inversores/pagos/${pid}`)).data; },
  async createSession(payload) { return (await api.post("/admin/inversores/sesiones", payload)).data; },
  async sessions(id) { return (await api.get(`/admin/inversores/${id}/sesiones`)).data; },
  async deleteSession(sid) { return (await api.delete(`/admin/inversores/sesiones/${sid}`)).data; },
  // Inversor (self)
  async me() { return (await api.get("/inversores/me")).data; },
  async updateMe(payload) { return (await api.put("/inversores/me/perfil", payload)).data; },
  async changePassword(payload) { return (await api.post("/inversores/me/password", payload)).data; },
  async uploadAvatar(formData) { return (await api.post("/inversores/me/avatar", formData)).data; },
  fileBlob(cid) { return api.get(`/inversores/me/archivo/${cid}`, { responseType: "blob" }).then((r) => r.data); },
  receiptBlob(pid) { return api.get(`/inversores/me/comprobante/${pid}`, { responseType: "blob" }).then((r) => r.data); },
};

export const colegiosApi = {
  // Admin (super admin / staff)
  async meta() { return (await api.get("/admin/colegios/meta")).data; },
  async listColegios() { return (await api.get("/admin/colegios")).data; },
  async createColegio(p) { return (await api.post("/admin/colegios", p)).data; },
  async updateColegio(id, p) { return (await api.put(`/admin/colegios/${id}`, p)).data; },
  async deleteColegio(id) { return (await api.delete(`/admin/colegios/${id}`)).data; },
  async addCiclo(id, p) { return (await api.post(`/admin/colegios/${id}/ciclos`, p)).data; },
  async delCiclo(id, cicloId) { return (await api.delete(`/admin/colegios/${id}/ciclos/${cicloId}`)).data; },
  async candidatos(id, programa = "", estado = "activos") { return (await api.get(`/admin/colegios/${id}/candidatos`, { params: { programa, estado } })).data; },
  async contenido(id) { return (await api.get(`/admin/colegios/${id}/contenido`)).data; },
  async listDocentes(colegioId = "") { return (await api.get("/admin/docentes", { params: colegioId ? { colegio_id: colegioId } : {} })).data; },
  async createDocente(p) { return (await api.post("/admin/docentes", p)).data; },
  async updateDocente(id, p) { return (await api.put(`/admin/docentes/${id}`, p)).data; },
  async deleteDocente(id) { return (await api.delete(`/admin/docentes/${id}`)).data; },
  async addNoticia(id, fd) { return (await api.post(`/admin/colegios/${id}/noticias`, fd)).data; },
  async addHeroe(id, fd) { return (await api.post(`/admin/colegios/${id}/heroes`, fd)).data; },
  async addSeguimiento(id, fd) { return (await api.post(`/admin/colegios/${id}/seguimiento`, fd)).data; },
  async addReunion(id, fd) { return (await api.post(`/admin/colegios/${id}/reuniones`, fd)).data; },
  async delContent(coll, itemId) { return (await api.delete(`/admin/colegios/contenido/${coll}/${itemId}`)).data; },
  async aviso(id, p) { return (await api.post(`/admin/colegios/${id}/aviso`, p)).data; },
  async resumen(id) { return (await api.get(`/admin/colegios/${id}/resumen`)).data; },
  reporteBlob(id) { return api.get(`/admin/colegios/${id}/reporte`, { responseType: "blob" }).then((r) => r.data); },
  myReporteBlob() { return api.get("/docentes/me/reporte", { responseType: "blob" }).then((r) => r.data); },
  // Docente (self)
  async me() { return (await api.get("/docentes/me")).data; },
  async myCandidatos(programa = "", estado = "activos") { return (await api.get("/docentes/me/candidatos", { params: { programa, estado } })).data; },
  async updateMe(p) { return (await api.put("/docentes/me/perfil", p)).data; },
  async changePassword(p) { return (await api.post("/docentes/me/password", p)).data; },
  async uploadAvatar(fd) { return (await api.post("/docentes/me/avatar", fd)).data; },
  fileBlob(coll, itemId) { return api.get(`/docentes/me/archivo/${coll}/${itemId}`, { responseType: "blob" }).then((r) => r.data); },
  recordingBlob(rid) { return api.get(`/connect/recordings/${rid}/stream`, { responseType: "blob" }).then((r) => r.data); },
};

export const fuerzasApi = {
  async me() { return (await api.get("/fuerzas/me")).data; },
  async savePlan(plan) { return (await api.put("/fuerzas/me/influencia", { plan })).data; },
  async addAccion(p) { return (await api.post("/fuerzas/me/influencia/accion", p)).data; },
  async delAccion(id) { return (await api.delete(`/fuerzas/me/influencia/accion/${id}`)).data; },
  async toggleDiaria(id) { return (await api.post(`/fuerzas/me/acciones/${id}/toggle`)).data; },
  async addLive(p) { return (await api.post("/fuerzas/me/lives", p)).data; },
  async delLive(id) { return (await api.delete(`/fuerzas/me/lives/${id}`)).data; },
  async addExpansion(p) { return (await api.post("/fuerzas/me/expansion", p)).data; },
  async delExpansion(id) { return (await api.delete(`/fuerzas/me/expansion/${id}`)).data; },
  videoBlob(id) { return api.get(`/fuerzas/me/video/${id}`, { responseType: "blob" }).then((r) => r.data); },
  // Admin
  async meta() { return (await api.get("/admin/fuerzas/meta")).data; },
  async users(prog) { return (await api.get(`/admin/fuerzas/${prog}/usuarios`)).data; },
  async videos(prog) { return (await api.get(`/admin/fuerzas/${prog}/videos`)).data; },
  async addVideo(prog, fd) { return (await api.post(`/admin/fuerzas/${prog}/videos`, fd)).data; },
  async delVideo(id) { return (await api.delete(`/admin/fuerzas/videos/${id}`)).data; },
  async userAcciones(uid) { return (await api.get(`/admin/fuerzas/usuarios/${uid}/acciones`)).data; },
  async addUserAccion(uid, p) { return (await api.post(`/admin/fuerzas/usuarios/${uid}/acciones`, p)).data; },
  async delUserAccion(id) { return (await api.delete(`/admin/fuerzas/acciones/${id}`)).data; },
  async userInfluencia(uid) { return (await api.get(`/admin/fuerzas/usuarios/${uid}/influencia`)).data; },
  async lives(prog) { return (await api.get(`/admin/fuerzas/${prog}/lives`)).data; },
  async expansion(prog) { return (await api.get(`/admin/fuerzas/${prog}/expansion`)).data; },
};

export const raceApi = {
  async config() { return (await api.get("/race/config")).data; },
  async saveRun(payload) { return (await api.post("/race/runs", payload)).data; },
  async ranking(mes) { return (await api.get(`/race/ranking?mes=${mes}`)).data; },
  async myRuns() { return (await api.get("/race/my-runs")).data; },
  async stats() { return (await api.get("/race/stats")).data; },
  async rankingTotal(year = 0) { return (await api.get(`/race/ranking-total?year=${year}`)).data; },
  async feed() { return (await api.get("/race/feed")).data; },
  async music() { return (await api.get("/race/music")).data; },
  musicUrl(id) { return `${BASE}/api/race/music/${id}`; },
  // Admin
  async adminConfig() { return (await api.get("/admin/race/config")).data; },
  async saveConfig(payload) { return (await api.put("/admin/race/config", payload)).data; },
  async adminRanking(mes, grupo = "") { return (await api.get(`/admin/race/ranking?mes=${mes}&grupo=${encodeURIComponent(grupo)}`)).data; },
  async adminRuns(params = {}) { const q = new URLSearchParams(params).toString(); return (await api.get(`/admin/race/runs?${q}`)).data; },
  async adminGroups() { return (await api.get("/admin/race/groups")).data; },
  async adminRankingTotal(year = 0, grupo = "") { return (await api.get(`/admin/race/ranking-total?year=${year}&grupo=${encodeURIComponent(grupo)}`)).data; },
};

export const splashApi = {
  async phrases() { return (await api.get("/splash/phrases")).data; },
  async getSettings() { return (await api.get("/admin/splash-settings")).data; },
  async setSettings(duration_ms) { return (await api.put("/admin/splash-settings", { duration_ms })).data; },
};

export function apiError(err, fallback = "Ocurrió un error. Intenta de nuevo.") {
  return err?.response?.data?.detail || fallback;
}

export function isAdmin() {
  const u = getUser();
  return ["super_admin", "admin", "manager", "staff"].includes(u?.role);
}

export function isSuperAdmin() {
  const u = getUser();
  return u?.role === "super_admin" || u?.role === "admin";
}

export function isAuthed() {
  return !!localStorage.getItem("supera_token");
}

export const userApi = {
  async reflexionesToday() {
    const { data } = await api.get("/reflexiones/today", { params: hijoParam() });
    return data;
  },
  async submitEvidencia(reflexionId, comentario) {
    const { data } = await api.post("/evidencias", { reflexion_id: reflexionId, comentario });
    return data;
  },
  async myEvidencias() {
    const { data } = await api.get("/evidencias/mine", { params: hijoParam() });
    return data;
  },
  async campusMine() {
    const { data } = await api.get("/campus/mine", { params: hijoParam() });
    return data;
  },
  async graduacion() {
    const { data } = await api.get("/graduacion", { params: hijoParam() });
    return data;
  },
  async submitActividadEvidencia(actividadId, comentario, imagen) {
    const { data } = await api.post("/evidencias/actividad", { actividad_id: actividadId, comentario, imagen });
    return data;
  },
  async ranking() {
    const { data } = await api.get("/ranking", { params: hijoParam() });
    return data;
  },
  async dashboardBadges() {
    const { data } = await api.get("/dashboard/badges", { params: hijoParam() });
    return data;
  },
  async fraseDia() {
    const { data } = await api.get("/frase-dia");
    return data;
  },
  async notifications() {
    const { data } = await api.get("/notifications");
    return data;
  },
  async unreadCount() {
    const { data } = await api.get("/notifications/unread-count");
    return data;
  },
  async markNotifRead(id) {
    const { data } = await api.post(`/notifications/${id}/read`);
    return data;
  },
  async markAllNotifRead() {
    const { data } = await api.post("/notifications/read-all");
    return data;
  },
  async me() {
    const { data } = await api.get("/auth/me");
    saveSession({ user: data });
    return data;
  },
};

export const adminApi = {
  audiencia: async (p) => (await api.post("/admin/push/audiencia", p)).data,
  async stats() {
    const { data } = await api.get("/admin/stats");
    return data;
  },  async listUsers(params = {}) {
    const { data } = await api.get("/admin/users", { params });
    return data;
  },
  async updateUser(id, payload) {
    const { data } = await api.put(`/admin/users/${id}`, payload);
    return data;
  },
  async deleteUser(id) {
    const { data } = await api.delete(`/admin/users/${id}`);
    return data;
  },
  async bulkGrupo(userIds, grupo) {
    const { data } = await api.post("/admin/users/bulk-grupo", { user_ids: userIds, grupo });
    return data;
  },
  async rbacSections() {
    const { data } = await api.get("/admin/rbac/sections");
    return data.sections;
  },
  async setUserRbac(id, payload) {
    const { data } = await api.put(`/admin/users/${id}/rbac`, payload);
    return data;
  },
  familias: {
    list: async (q = "") => (await api.get("/admin/familias", { params: { q } })).data,
    searchCandidatos: async (q = "") => (await api.get("/admin/familias/candidatos", { params: { q } })).data,
    vincular: async (parentId, candidatoId) => (await api.post(`/admin/familias/${parentId}/vincular`, { candidato_id: candidatoId })).data,
    desvincular: async (parentId, candidatoId) => (await api.delete(`/admin/familias/${parentId}/hijo/${candidatoId}`)).data,
    deleteParent: async (parentId) => (await api.delete(`/admin/familias/${parentId}`)).data,
    getDesarrollo: async (candidatoId) => (await api.get(`/admin/familias/desarrollo/${candidatoId}`)).data,
    addRetro: async (candidatoId, texto) => (await api.post(`/admin/familias/desarrollo/${candidatoId}/retro`, { texto })).data,
    delRetro: async (candidatoId, itemId) => (await api.delete(`/admin/familias/desarrollo/${candidatoId}/retro/${itemId}`)).data,
    addDialogo: async (candidatoId, payload) => (await api.post(`/admin/familias/desarrollo/${candidatoId}/dialogo`, payload)).data,
    editDialogo: async (candidatoId, itemId, payload) => (await api.put(`/admin/familias/desarrollo/${candidatoId}/dialogo/${itemId}`, payload)).data,
    delDialogo: async (candidatoId, itemId) => (await api.delete(`/admin/familias/desarrollo/${candidatoId}/dialogo/${itemId}`)).data,
    listContenido: async (seccion = "") => (await api.get("/admin/familias/contenido", { params: { seccion } })).data,
    createContenido: async (p) => (await api.post("/admin/familias/contenido", p)).data,
    updateContenido: async (id, p) => (await api.put(`/admin/familias/contenido/${id}`, p)).data,
    delContenido: async (id) => (await api.delete(`/admin/familias/contenido/${id}`)).data,
    listEval: async (tipo = "") => (await api.get("/admin/familias/eval", { params: { tipo } })).data,
    createEval: async (p) => (await api.post("/admin/familias/eval", p)).data,
    updateEval: async (id, p) => (await api.put(`/admin/familias/eval/${id}`, p)).data,
    delEval: async (id) => (await api.delete(`/admin/familias/eval/${id}`)).data,
    listEvalRespuestas: async (tipo = "") => (await api.get("/admin/familias/eval-respuestas", { params: { tipo } })).data,
    delEvalRespuesta: async (id) => (await api.delete(`/admin/familias/eval-respuestas/${id}`)).data,
    runWeeklySummary: async (parentId) => (await api.post(`/admin/familias/weekly-summary/run`, parentId ? { parent_id: parentId } : {})).data,
  },
  async superaxUploadImage(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/superax/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  },
  userRoles: {
    list: async () => (await api.get("/admin/user-roles")).data,    create: async (p) => (await api.post("/admin/user-roles", p)).data,
    update: async (id, p) => (await api.put(`/admin/user-roles/${id}`, p)).data,
    remove: async (id) => (await api.delete(`/admin/user-roles/${id}`)).data,
  },
  userSegments: {
    list: async () => (await api.get("/admin/user-segments")).data,
    create: async (p) => (await api.post("/admin/user-segments", p)).data,
    update: async (id, p) => (await api.put(`/admin/user-segments/${id}`, p)).data,
    remove: async (id) => (await api.delete(`/admin/user-segments/${id}`)).data,
  },
  resource(name) {
    return {
      async list() {
        const { data } = await api.get(`/admin/${name}`);
        return data;
      },
      async create(payload) {
        const { data } = await api.post(`/admin/${name}`, payload);
        return data;
      },
      async update(id, payload) {
        const { data } = await api.put(`/admin/${name}/${id}`, payload);
        return data;
      },
      async remove(id) {
        const { data } = await api.delete(`/admin/${name}/${id}`);
        return data;
      },
    };
  },
  async getAssistantConfig() {
    const { data } = await api.get("/admin/assistant/config");
    return data;
  },
  async updateAssistantConfig(instructions) {
    const { data } = await api.put("/admin/assistant/config", { instructions });
    return data;
  },
  assistantFaqs() {
    return this.resource("assistant/faqs");
  },
  reflexiones() {
    return this.resource("reflexiones");
  },
  gradActividades() {
    return this.resource("graduacion/actividades");
  },
  gradReconocimientos() {
    return this.resource("graduacion/reconocimientos");
  },
  async getGradConfig() {
    const { data } = await api.get("/admin/graduacion/config");
    return data;
  },
  async updateGradConfig(payload) {
    const { data } = await api.put("/admin/graduacion/config", payload);
    return data;
  },
  async listEvidencias(status = "all", page = 1, limit = 30) {
    const { data } = await api.get(`/admin/evidencias`, { params: { status, page, limit } });
    return data;
  },
  async evidenciaImagen(id) {
    const { data } = await api.get(`/admin/evidencias/${id}/imagen`);
    return data;
  },
  async reviewEvidencia(id, payload) {
    const { data } = await api.put(`/admin/evidencias/${id}/review`, payload);
    return data;
  },
  async deleteEvidencia(id) {
    const { data } = await api.delete(`/admin/evidencias/${id}`);
    return data;
  },
  async deleteEvidenciasBulk(payload) {
    const { data } = await api.post(`/admin/evidencias/delete-bulk`, payload);
    return data;
  },
  async listGradEvidencias(status = "all", page = 1, limit = 30) {
    const { data } = await api.get(`/admin/graduacion/evidencias`, { params: { status, page, limit } });
    return data;
  },
  async deleteGradEvidencia(id) {
    const { data } = await api.delete(`/admin/graduacion/evidencias/${id}`);
    return data;
  },
  async deleteGradEvidenciasBulk(payload) {
    const { data } = await api.post(`/admin/graduacion/evidencias/delete-bulk`, payload);
    return data;
  },
  referencias() {
    return this.resource("referencias");
  },
  async bulkReferencias(payload) {
    const { data } = await api.post("/admin/referencias/bulk", payload);
    return data;
  },
  frases() {
    return this.resource("frases");
  },
  splashPhrases() {
    return this.resource("splash-phrases");
  },
  campus: {
    bulkBlogs: async (p) => (await api.post("/admin/campus/blogs/bulk", p)).data,
    contratos: async () => (await api.get("/admin/campus/contratos")).data,
    createContrato: async (p) => (await api.post("/admin/campus/contratos", p)).data,
    updateContrato: async (id, p) => (await api.put(`/admin/campus/contratos/${id}`, p)).data,
    removeContrato: async (id) => (await api.delete(`/admin/campus/contratos/${id}`)).data,
    uploadContrato: async (file) => { const fd = new FormData(); fd.append("file", file); return (await api.post("/admin/campus/contratos/upload", fd)).data; },
    uploadBlogMedia: async (file) => { const fd = new FormData(); fd.append("file", file); return (await api.post("/admin/campus/blog-media", fd)).data; },
    instructorPagos: async (instructorId) => (await api.get("/admin/campus/instructor-pagos", { params: instructorId ? { instructor_id: instructorId } : {} })).data,
    createInstructorPago: async (p) => (await api.post("/admin/campus/instructor-pagos", p)).data,
    updateInstructorPago: async (id, p) => (await api.put(`/admin/campus/instructor-pagos/${id}`, p)).data,
    removeInstructorPago: async (id) => (await api.delete(`/admin/campus/instructor-pagos/${id}`)).data,
    instructorMensajes: async (estado) => (await api.get("/admin/campus/instructor-mensajes", { params: estado ? { estado } : {} })).data,
    moderarMensaje: async (id, estado) => (await api.put(`/admin/campus/instructor-mensajes/${id}/moderar`, { estado })).data,
    removeMensaje: async (id) => (await api.delete(`/admin/campus/instructor-mensajes/${id}`)).data,
    seguimiento: async () => (await api.get("/admin/campus/seguimiento")).data,
    recordatorio: async (payload) => (await api.post("/admin/campus/seguimiento/recordatorio", payload)).data,
    recordatorioMasivo: async (payload) => (await api.post("/admin/campus/seguimiento/recordatorio-masivo", payload)).data,
    recordatorioPlantillas: async () => (await api.get("/admin/campus/recordatorio-plantillas")).data,
    recordatorioRules: async () => (await api.get("/admin/campus/recordatorio-rules")).data,
    createRecordatorioRule: async (p) => (await api.post("/admin/campus/recordatorio-rules", p)).data,
    updateRecordatorioRule: async (id, p) => (await api.put(`/admin/campus/recordatorio-rules/${id}`, p)).data,
    removeRecordatorioRule: async (id) => (await api.delete(`/admin/campus/recordatorio-rules/${id}`)).data,
    recordatorioHistorial: async (ruleId) => (await api.get("/admin/campus/recordatorio-historial", { params: ruleId ? { rule_id: ruleId } : {} })).data,
  },
  drive: {
    me: async () => (await api.get("/admin/drive/me")).data,
    usage: async () => (await api.get("/admin/drive/usage")).data,
    folders: async (parentId = "") => (await api.get("/admin/drive/folders", { params: { parent_id: parentId } })).data,
    foldersAll: async () => (await api.get("/admin/drive/folders-all")).data,
    createFolder: async (nombre, parentId = "") => (await api.post("/admin/drive/folders", { nombre, parent_id: parentId })).data,
    updateFolder: async (id, p) => (await api.put(`/admin/drive/folders/${id}`, p)).data,
    removeFolder: async (id) => (await api.delete(`/admin/drive/folders/${id}`)).data,
    files: async (params = {}) => (await api.get("/admin/drive/files", { params })).data,
    updateFile: async (id, p) => (await api.put(`/admin/drive/files/${id}`, p)).data,
    share: async (id, enabled) => (await api.put(`/admin/drive/files/${id}/share`, { enabled })).data,
    removeFile: async (id) => (await api.delete(`/admin/drive/files/${id}`)).data,
    blob: async (id) => (await api.get(`/admin/drive/files/${id}/download`, { responseType: "blob" })).data,
    downloadUrl: (id) => `${BASE}/api/admin/drive/files/${id}/download`,
    staff: async () => (await api.get("/admin/drive/staff")).data,
    setStaff: async (email, p) => (await api.put(`/admin/drive/staff/${encodeURIComponent(email)}`, p)).data,
    uploadInit: async () => (await api.post("/admin/drive/upload/init")).data,
    uploadChunk: async (uploadId, index, chunk) => {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", index);
      fd.append("chunk", chunk);
      return (await api.post("/admin/drive/upload/chunk", fd)).data;
    },
    uploadComplete: async (payload) => (await api.post("/admin/drive/upload/complete", payload)).data,
  },
  async pushSend(payload) {
    const { data } = await api.post("/admin/push/send", payload);
    return data;
  },
  async pushCampaigns() {
    const { data } = await api.get("/admin/push/campaigns");
    return data;
  },
  async pushReceipts(cid) {
    const { data } = await api.get(`/admin/push/campaigns/${cid}/receipts`);
    return data;
  },
  scheduled() {
    return {
      list: async () => (await api.get("/admin/notificaciones-programadas")).data,
      create: async (payload) => (await api.post("/admin/notificaciones-programadas", payload)).data,
      update: async (id, payload) => (await api.put(`/admin/notificaciones-programadas/${id}`, payload)).data,
      remove: async (id) => (await api.delete(`/admin/notificaciones-programadas/${id}`)).data,
    };
  },
  finance: {
    getConfig: async () => (await api.get("/admin/finance/config")).data,
    saveConfig: async (p) => (await api.put("/admin/finance/config", p)).data,
    programs: async () => (await api.get("/admin/finance/programs")).data,
    createProgram: async (p) => (await api.post("/admin/finance/programs", p)).data,
    updateProgram: async (id, p) => (await api.put(`/admin/finance/programs/${id}`, p)).data,
    removeProgram: async (id) => (await api.delete(`/admin/finance/programs/${id}`)).data,
    manualIncome: async () => (await api.get("/admin/finance/manual-income")).data,
    createManualIncome: async (p) => (await api.post("/admin/finance/manual-income", p)).data,
    updateManualIncome: async (id, p) => (await api.put(`/admin/finance/manual-income/${id}`, p)).data,
    removeManualIncome: async (id) => (await api.delete(`/admin/finance/manual-income/${id}`)).data,
    accounts: async (params = {}) => (await api.get("/admin/finance/accounts", { params })).data,
    accountDetail: async (uid) => (await api.get(`/admin/finance/accounts/${uid}`)).data,
    updateAccount: async (uid, p) => (await api.put(`/admin/finance/accounts/${uid}`, p)).data,
    payments: async (params = {}) => (await api.get("/admin/finance/payments", { params })).data,
    reviewPayment: async (id, p) => (await api.put(`/admin/finance/payments/${id}/review`, p)).data,
    aids: async () => (await api.get("/admin/finance/aids")).data,
    createAid: async (p) => (await api.post("/admin/finance/aids", p)).data,
    updateAid: async (id, p) => (await api.put(`/admin/finance/aids/${id}`, p)).data,
    removeAid: async (id) => (await api.delete(`/admin/finance/aids/${id}`)).data,
    concepts: async () => (await api.get("/admin/finance/concepts")).data,
    createConcept: async (p) => (await api.post("/admin/finance/concepts", p)).data,
    updateConcept: async (id, p) => (await api.put(`/admin/finance/concepts/${id}`, p)).data,
    removeConcept: async (id) => (await api.delete(`/admin/finance/concepts/${id}`)).data,
    receiptBlobUrl: async (path) => {
      const { data } = await api.get(`/finance/receipt/${path}`, { responseType: "blob" });
      return URL.createObjectURL(data);
    },
    dashboard: async (periodo = "mes") => (await api.get("/admin/finance/dashboard", { params: { periodo } })).data,
    exportCsv: async (tipo) => {
      const { data } = await api.get("/admin/finance/export", { params: { tipo }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `supera_${tipo}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    },
  },
};

export const familiaApi = {
  misHijos: async () => (await api.get("/familia/mis-hijos")).data,
  desarrollo: async (candidatoId) => (await api.get(`/familia/desarrollo/${candidatoId}`)).data,
  contenido: async (seccion) => (await api.get("/familia/contenido", { params: { seccion } })).data,
  evalPreguntas: async (tipo) => (await api.get("/familia/eval", { params: { tipo } })).data,
  evalSubmit: async (payload) => (await api.post("/familia/eval", payload)).data,
};


export const financeApi = {
  status: async (hijoId) => (await api.get("/finance/status", { params: hijoId ? { hijo_id: hijoId } : {} })).data,
  payInfo: async () => (await api.get("/finance/pay-info")).data,
  statement: async (hijoId) => (await api.get("/finance/statement", { params: hijoId ? { hijo_id: hijoId } : {} })).data,
  aids: async (hijoId) => (await api.get("/finance/aids", { params: hijoId ? { hijo_id: hijoId } : {} })).data,
  concepts: async (hijoId) => (await api.get("/finance/concepts", { params: hijoId ? { hijo_id: hijoId } : {} })).data,
  myPayments: async (hijoId) => (await api.get("/finance/payments/mine", { params: hijoId ? { hijo_id: hijoId } : {} })).data,
  upload: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/finance/upload", fd);
    return data;
  },
  submitPayment: async (payload) => (await api.post("/finance/payments", payload)).data,
  receiptBlobUrl: async (path) => {
    const { data } = await api.get(`/finance/receipt/${path}`, { responseType: "blob" });
    return URL.createObjectURL(data);
  },
};

export function mediaUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BASE}${path}`;
}

export const profileApi = {
  update: async (payload) => (await api.put("/me/profile", payload)).data,
  updateAdminProfile: async (payload) => (await api.put("/me/admin-profile", payload)).data,
  changePassword: async (current_password, new_password) =>
    (await api.post("/me/password", { current_password, new_password })).data,
  uploadAvatar: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/me/avatar", fd);
    return data;
  },
};

export function isStaff() {
  const u = getUser();
  return u?.role === "staff";
}

export function isStaffOrAdmin() {
  const u = getUser();
  return ["super_admin", "admin", "manager", "staff"].includes(u?.role);
}

export const chatApi = {
  intro: async () => (await api.get("/chat/intro")).data,
  family: async () => (await api.get("/chat/family")).data,
  inviteFamily: async (email) => (await api.post("/chat/family/invite", { email })).data,
  acceptInvite: async (id) => (await api.post(`/chat/family/invites/${id}/accept`)).data,
  rejectInvite: async (id) => (await api.post(`/chat/family/invites/${id}/reject`)).data,
  conversations: async () => (await api.get("/chat/conversations")).data,
  createConversation: async (payload) => (await api.post("/chat/conversations", payload)).data,
  conversation: async (id) => (await api.get(`/chat/conversations/${id}`)).data,
  messages: async (id, after) => (await api.get(`/chat/conversations/${id}/messages`, { params: after ? { after } : {} })).data,
  sendMessage: async (id, payload) => (await api.post(`/chat/conversations/${id}/messages`, payload)).data,
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post("/chat/upload", fd)).data;
  },
  close: async (id) => (await api.post(`/chat/conversations/${id}/close`)).data,
  survey: async (id, payload) => (await api.post(`/chat/conversations/${id}/survey`, payload)).data,
  unreadCount: async () => (await api.get("/chat/unread-count")).data,
};

export const staffChatApi = {
  queue: async () => (await api.get("/chat/staff/queue")).data,
  claim: async (id) => (await api.post(`/chat/conversations/${id}/claim`)).data,
  getAvailability: async () => (await api.get("/chat/staff/availability")).data,
  setAvailability: async (available) => (await api.put("/chat/staff/availability", null, { params: { available } })).data,
  conversation: async (id) => (await api.get(`/chat/conversations/${id}`)).data,
  messages: async (id, after) => (await api.get(`/chat/conversations/${id}/messages`, { params: after ? { after } : {} })).data,
  sendMessage: async (id, payload) => (await api.post(`/chat/conversations/${id}/messages`, payload)).data,
  uploadImage: chatApi.uploadImage,
  close: async (id) => (await api.post(`/chat/conversations/${id}/close`)).data,
};

export const adminChatApi = {
  getConfig: async () => (await api.get("/chat/admin/config")).data,
  saveConfig: async (p) => (await api.put("/chat/admin/config", p)).data,
  conversations: async (params = {}) => (await api.get("/chat/admin/conversations", { params })).data,
  deleteConversation: async (id) => (await api.delete(`/chat/admin/conversations/${id}`)).data,
  listStaff: async () => (await api.get("/chat/admin/staff")).data,
  createStaff: async (p) => (await api.post("/chat/admin/staff", p)).data,
  deleteStaff: async (id) => (await api.delete(`/chat/admin/staff/${id}`)).data,
  families: async () => (await api.get("/chat/admin/families")).data,
  revokeFamily: async (id) => (await api.post(`/chat/admin/families/${id}/revoke`)).data,
  surveys: async () => (await api.get("/chat/admin/surveys")).data,
  autoReplies: async () => (await api.get("/chat/admin/auto-replies")).data,
  createAutoReply: async (p) => (await api.post("/chat/admin/auto-replies", p)).data,
  updateAutoReply: async (id, p) => (await api.put(`/chat/admin/auto-replies/${id}`, p)).data,
  deleteAutoReply: async (id) => (await api.delete(`/chat/admin/auto-replies/${id}`)).data,
};

export const compromisoApi = {
  me: async () => (await api.get("/compromiso/me", { params: hijoParam() })).data,
  mark: async (actividad_id, slot) => (await api.post("/compromiso/me/mark", { actividad_id, slot })).data,
  unmark: async (id) => (await api.delete(`/compromiso/me/mark/${id}`)).data,
  submit: async () => (await api.post("/compromiso/me/submit")).data,
  linkParent: async (email) => (await api.post("/compromiso/me/link-parent", { email })).data,
  history: async () => (await api.get("/compromiso/me/history", { params: hijoParam() })).data,
  parentPending: async () => (await api.get("/compromiso/parent/pending")).data,
  parentValidate: async (id, approve) => (await api.post(`/compromiso/parent/validate/${id}`, { approve })).data,
  parentValidateSheet: async (id, approve) => (await api.post(`/compromiso/parent/validate-sheet/${id}`, { approve })).data,
};

export const compromisoAdminApi = {
  templates: async () => (await api.get("/compromiso/admin/templates")).data,
  createTemplate: async (programa) => (await api.post("/compromiso/admin/templates", null, { params: { programa } })).data,
  updateTemplate: async (id, p) => (await api.put(`/compromiso/admin/templates/${id}`, p)).data,
  deleteTemplate: async (id) => (await api.delete(`/compromiso/admin/templates/${id}`)).data,
  getConfig: async () => (await api.get("/compromiso/admin/config")).data,
  saveConfig: async (p) => (await api.put("/compromiso/admin/config", p)).data,
  status: async (programa) => (await api.get("/compromiso/admin/status", { params: programa ? { programa } : {} })).data,
  userDetail: async (uid) => (await api.get(`/compromiso/admin/user/${uid}`)).data,
  extend: async (p) => (await api.post("/compromiso/admin/extend", p)).data,
  broadcast: async (p) => (await api.post("/compromiso/admin/broadcast", p)).data,
};

export const campusApi = {
  overview: async () => (await api.get("/campus/overview", { params: hijoParam() })).data,
  marcarVisto: async (claseId) => (await api.post(`/campus/sesion/${claseId}/visto`)).data,
  getEvaluacion: async (claseId) => (await api.get(`/campus/sesion/${claseId}/evaluacion`)).data,
  submitEvaluacion: async (claseId, texto, respuestas = {}) => (await api.post(`/campus/sesion/${claseId}/evaluacion`, { texto, respuestas })).data,
  submitEvidencia: async (claseId, payload) => (await api.post(`/campus/sesion/${claseId}/evidencia`, payload)).data,
  // LOTE D: Libro Mensual + Biblioteca
  libros: async () => (await api.get("/campus/libros", { params: hijoParam() })).data,
  submitLibro: async (libroId, payload) => (await api.post(`/campus/libros/${libroId}/entrega`, payload)).data,
  biblioteca: async (tag) => (await api.get("/campus/biblioteca", { params: hijoParam(tag ? { tag } : {}) })).data,
  evalInstructores: async () => (await api.get("/campus/eval-instructores", { params: hijoParam() })).data,
  submitEvalInstructor: async (payload) => (await api.post("/campus/eval-instructor", payload)).data,
  blogs: async () => (await api.get("/campus/blogs", { params: hijoParam() })).data,
  videosExpress: async () => (await api.get("/campus/videos-express", { params: hijoParam() })).data,
  blogComentario: async (id, comentario) => (await api.post(`/campus/blog/${id}/comentario`, { comentario })).data,
  videoComentario: async (id, comentario) => (await api.post(`/campus/video-express/${id}/comentario`, { comentario })).data,
  instructorMensajes: async () => (await api.get("/campus/instructor-mensajes")).data,
  postInstructorMensaje: async (payload) => (await api.post("/campus/instructor-mensajes", payload)).data,
  libroEntregas: async (status) => (await api.get("/admin/campus/libro-entregas", { params: status ? { status } : {} })).data,
  reviewLibroEntrega: async (id, payload) => (await api.put(`/admin/campus/libro-entregas/${id}/review`, payload)).data,
  feedbackTemplates: async () => (await api.get("/admin/campus/feedback-templates")).data,
  saveFeedbackTemplates: async (templates) => (await api.put("/admin/campus/feedback-templates", { templates })).data,
  setInicio: async (email, inicio) => (await api.put("/admin/campus/user-inicio", { email, inicio })).data,
  bulkClases: async (payload) => (await api.post("/admin/campus/clases/bulk", payload)).data,
  claustro: async () => (await api.get("/campus/claustro")).data,
  downloadIcs: async (claseId, filename = "sesion-supera.ics") => {
    const { data } = await api.get(`/campus/sesion/${claseId}/ics`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
  // Admin: claustro de instructores
  instructores: async () => (await api.get("/admin/campus/instructores")).data,
  createInstructor: async (p) => (await api.post("/admin/campus/instructores", p)).data,
  updateInstructor: async (id, p) => (await api.put(`/admin/campus/instructores/${id}`, p)).data,
  deleteInstructor: async (id) => (await api.delete(`/admin/campus/instructores/${id}`)).data,
  uploadInstructorFoto: async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    return (await api.post(`/admin/campus/instructores/${id}/foto`, fd)).data;
  },
  uploadInstructorCv: async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    return (await api.post(`/admin/campus/instructores/${id}/cv`, fd)).data;
  },
  // Admin: config Campus + encuestas + evaluaciones (LOTE C)
  getConfig: async () => (await api.get("/admin/campus/config")).data,
  saveConfig: async (p) => (await api.put("/admin/campus/config", p)).data,
  listEncuestas: async () => (await api.get("/admin/campus/encuestas")).data,
  listEvaluaciones: async (claseId) => (await api.get("/admin/campus/evaluaciones", { params: claseId ? { clase_id: claseId } : {} })).data,
  evalResumen: async () => (await api.get("/admin/campus/eval-instructores/resumen")).data,
  shareRanking: async (id, visible) => (await api.put(`/admin/campus/instructores/${id}/compartir-ranking`, { visible })).data,
  shareRankingAll: async (visible) => (await api.post("/admin/campus/instructores/compartir-ranking-todos", { visible })).data,
  // Super Admin: repositorio y borrado de evidencias de Campus (no afecta puntos)
  listClaseEvidencias: async (status = "all", page = 1, limit = 30) => (await api.get("/admin/campus/evidencias", { params: { status, page, limit } })).data,
  deleteClaseEvidencia: async (id) => (await api.delete(`/admin/campus/evidencias/${id}`)).data,
  deleteClaseEvidenciasBulk: async (payload) => (await api.post("/admin/campus/evidencias/delete-bulk", payload)).data,
  deleteEvaluacion: async (id) => (await api.delete(`/admin/campus/evaluaciones/${id}`)).data,
  deleteEvaluacionesBulk: async (payload) => (await api.post("/admin/campus/evaluaciones/delete-bulk", payload)).data,
  deleteLibroEntrega: async (id) => (await api.delete(`/admin/campus/libro-entregas/${id}`)).data,
  deleteLibroEntregasBulk: async (payload) => (await api.post("/admin/campus/libro-entregas/delete-bulk", payload)).data,
};

export function isInstructor() {
  const u = getUser();
  return u?.role === "instructor";
}

export const superaxApi = {
  list: async () => (await api.get("/superax")).data,
  adminList: async () => (await api.get("/admin/superax")).data,
  create: async (p) => (await api.post("/admin/superax", p)).data,
  update: async (id, p) => (await api.put(`/admin/superax/${id}`, p)).data,
  remove: async (id) => (await api.delete(`/admin/superax/${id}`)).data,
};

export const instructorApi = {
  me: async () => (await api.get("/instructor/me")).data,
  miEvaluacion: async () => (await api.get("/instructor/mi-evaluacion")).data,
  encuestasAlumnos: async () => (await api.get("/instructor/encuestas-alumnos")).data,
  encuestas: async () => (await api.get("/instructor/encuestas")).data,
  submitEncuesta: async (payload) => (await api.post("/instructor/encuestas", payload)).data,
  uploadMaterial: async (claseId, titulo, file) => {
    const fd = new FormData();
    fd.append("titulo", titulo);
    fd.append("file", file);
    return (await api.post(`/instructor/clases/${claseId}/materiales/upload`, fd)).data;
  },
  linkMaterial: async (claseId, payload) => (await api.post(`/instructor/clases/${claseId}/materiales/link`, payload)).data,
  deleteMaterial: async (mid) => (await api.delete(`/instructor/materiales/${mid}`)).data,
  grabaciones: async () => (await api.get("/instructor/grabaciones")).data,
  materiales: async () => (await api.get("/instructor/materiales-general")).data,
  uploadMaterialGeneral: async (titulo, file) => {
    const fd = new FormData(); fd.append("titulo", titulo); fd.append("file", file);
    return (await api.post("/instructor/materiales-general/upload", fd)).data;
  },
  linkMaterialGeneral: async (payload) => (await api.post("/instructor/materiales-general/link", payload)).data,
  deleteMaterialGeneral: async (mid) => (await api.delete(`/instructor/materiales-general/${mid}`)).data,
  contratacion: async () => (await api.get("/instructor/contratacion")).data,
  pagos: async () => (await api.get("/instructor/pagos")).data,
  mensajes: async () => (await api.get("/instructor/mensajes")).data,
  responderMensaje: async (payload) => (await api.post("/instructor/mensajes/responder", payload)).data,
};

export const connectApi = {
  config: async () => (await api.get("/connect/config")).data,
  list: async () => (await api.get("/connect/sessions")).data,
  instructors: async () => (await api.get("/connect/instructors")).data,
  live: async () => (await api.get("/connect/live")).data,
  get: async (id) => (await api.get(`/connect/sessions/${id}`)).data,
  create: async (p) => (await api.post("/connect/sessions", p)).data,
  update: async (id, p) => (await api.put(`/connect/sessions/${id}`, p)).data,
  start: async (id) => (await api.post(`/connect/sessions/${id}/start`)).data,
  end: async (id) => (await api.post(`/connect/sessions/${id}/end`)).data,
  remove: async (id) => (await api.delete(`/connect/sessions/${id}`)).data,
  joinToken: async (id) => (await api.post(`/connect/sessions/${id}/join-token`)).data,
  join: async (id, uid) => (await api.post(`/connect/sessions/${id}/join`, { uid })).data,
  leave: async (id, uid) => (await api.post(`/connect/sessions/${id}/leave`, { uid })).data,
  heartbeat: async (id, uid) => (await api.post(`/connect/sessions/${id}/heartbeat`, { uid })).data,
  participants: async (id) => (await api.get(`/connect/sessions/${id}/participants`)).data,
  recordings: async () => (await api.get("/connect/recordings")).data,
  updateRecording: async (rid, p) => (await api.put(`/connect/recordings/${rid}`, p)).data,
  deleteRecording: async (rid) => (await api.delete(`/connect/recordings/${rid}`)).data,
  recStreamBlob: async (rid) => (await api.get(`/connect/recordings/${rid}/stream`, { responseType: "blob" })).data,
  campusClasses: async () => (await api.get("/connect/campus-classes")).data,
  linkClass: async (id, materiaId) => (await api.post(`/connect/sessions/${id}/link`, { materia_id: materiaId })).data,
};


export const assistantApi = {
  async chat(message, sessionId) {
    const { data } = await api.post("/assistant/chat", { message, session_id: sessionId });
    return data;
  },
};

export function getAssistantSession() {
  let s = localStorage.getItem("supera_assistant_session");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("supera_assistant_session", s);
  }
  return s;
}

export function updateStoredUser(patch) {
  const u = getUser();
  if (u) localStorage.setItem(USER_KEY, JSON.stringify({ ...u, ...patch }));
}

export const capacitacionApi = {
  estado: async () => (await api.get("/capacitaciones/estado")).data,
  list: async () => (await api.get("/capacitaciones")).data,
  get: async (id) => (await api.get(`/capacitaciones/${id}`)).data,
  evaluar: async (id, respuestas) => (await api.post(`/capacitaciones/${id}/evaluar`, { respuestas })).data,
};

export const capacitacionPublicApi = {
  info: async (token) => (await api.get(`/public/capacitaciones/${token}`)).data,
  acceso: async (token, payload) => (await api.post(`/public/capacitaciones/${token}/acceso`, payload)).data,
  evaluar: async (token, id, payload) => (await api.post(`/public/capacitaciones/${token}/${id}/evaluar`, payload)).data,
};

export const capacitacionAdminApi = {
  list: async () => (await api.get("/admin/capacitaciones")).data,
  create: async (p) => (await api.post("/admin/capacitaciones", p)).data,
  update: async (id, p) => (await api.put(`/admin/capacitaciones/${id}`, p)).data,
  remove: async (id) => (await api.delete(`/admin/capacitaciones/${id}`)).data,
  progreso: async (params) => (await api.get("/admin/capacitaciones/progreso", { params })).data,
  progresoUser: async (uid) => (await api.get(`/admin/capacitaciones/progreso/${uid}`)).data,
  setLock: async (uid, bloqueado) => (await api.put(`/admin/capacitaciones/usuario/${uid}/bloqueo`, { bloqueado })).data,
  remind: async (user_ids) => (await api.post("/admin/capacitaciones/recordatorio", { user_ids })).data,
  settings: async () => (await api.get("/admin/capacitaciones/settings")).data,
  saveSettings: async (p) => (await api.put("/admin/capacitaciones/settings", p)).data,
  publicResults: async () => (await api.get("/admin/capacitaciones/publico-resultados")).data,
};

export const anaApi = {
  config: async () => (await api.get("/ana/config")).data,
  historial: async (hijo_id) => (await api.get("/ana/historial", { params: hijo_id ? { hijo_id } : {} })).data,
  preguntar: async (pregunta, hijo_id, nueva_sesion) => (await api.post("/ana/preguntar", { pregunta, hijo_id: hijo_id || null, nueva_sesion: !!nueva_sesion })).data,
  reporte: async (id) => (await api.get(`/ana/reporte/${id}`)).data,
};

export const anaAdminApi = {
  settings: async () => (await api.get("/admin/ana/settings")).data,
  save: async (p) => (await api.put("/admin/ana/settings", p)).data,
  conversaciones: async (params) => (await api.get("/admin/ana/conversaciones", { params })).data,
  borrar: async (id) => (await api.delete(`/admin/ana/conversaciones/${id}`)).data,
  borrarVarias: async (payload) => (await api.post("/admin/ana/conversaciones/borrar", payload)).data,
  uploadFoto: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post("/admin/superax/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.url;
  },
};
